import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaTimes } from 'react-icons/fa';
import { useSettingsStore } from '../../stores/settingsStore';
import type { SupportedLanguage } from '../../services/languageService';
import { SUPPORTED_LANGUAGES } from '../../services/languageService';
import { Switch } from '@headlessui/react';
import { useTheme } from '../../contexts/ThemeContext';

interface SettingsProps {
  onClose: () => void;
}

const Settings: React.FC<SettingsProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const { mode, setTheme } = useTheme();
  const isNeonTheme = mode === 'neon';
  const isFairyTheme = mode === 'fairy';
  const {
    language,
    setLanguage,
    pushNotificationsEnabled,
    setPushNotificationsEnabled,
  } = useSettingsStore();

  const handleLanguageChange = async (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newLanguage = event.target.value as SupportedLanguage;
    try {
      setLanguage(newLanguage);
      document.documentElement.lang = newLanguage;
    } catch (error) {
      console.error('Failed to change language:', error);
    }
  };

  return (
    <div className="absolute top-full right-0 mt-2 w-80 z-[150]">
      <div className={`rounded-lg shadow-lg overflow-hidden ${isNeonTheme
        ? 'bg-black border border-[#00FF00]/20'
        : isFairyTheme
          ? 'bg-[#1a1625] border border-purple-500/20'
          : 'bg-gray-800 border border-gray-700'
        }`}>
        <div className="p-4 border-b border-gray-700 flex items-center justify-between">
          <h2 className={`text-lg font-semibold ${isNeonTheme
            ? 'text-[#00FF00]'
            : isFairyTheme
              ? 'text-purple-200'
              : 'text-white'
            }`}>
            {t('settings.title')}
          </h2>
          <button
            onClick={onClose}
            className={`p-1.5 rounded-full transition-colors ${isNeonTheme
              ? 'text-[#00FF00] hover:bg-[#00FF00]/20'
              : isFairyTheme
                ? 'text-purple-200 hover:bg-purple-500/20'
                : 'text-gray-400 hover:bg-gray-700'
              }`}
          >
            <FaTimes className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 space-y-6">
          {/* Language */}
          <div>
            <h3 className={`text-sm font-medium mb-2 ${isFairyTheme ? 'text-purple-200' : 'text-white'}`}>
              {t('settings.language.app_language')}
            </h3>
            <select
              value={language}
              onChange={handleLanguageChange}
              className={`w-full px-3 py-2 rounded-lg ${isFairyTheme
                ? 'bg-[#1a1625] border border-purple-500/20 text-purple-200'
                : 'bg-gray-700 border border-gray-600 text-white'
                }`}
            >
              {Object.entries(SUPPORTED_LANGUAGES).map(([code, name]) => (
                <option key={code} value={code}>
                  {name}
                </option>
              ))}
            </select>
          </div>

          {/* Theme */}
          <div>
            <h3 className={`text-sm font-medium mb-2 ${isNeonTheme
              ? 'text-[#00FF00]'
              : isFairyTheme
                ? 'text-purple-200'
                : 'text-white'
              }`}>
              {t('settings.appearance.theme')}
            </h3>
            <div className="flex flex-col space-y-2">
              {/* Default Theme */}
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm ${mode === 'default' ? 'text-primary' : 'text-gray-400'}`}>
                    {t('settings.appearance.default_theme')}
                  </p>
                </div>
                <Switch
                  checked={mode === 'default'}
                  onChange={() => setTheme('default')}
                  className={`${mode === 'default' ? 'bg-primary' : 'bg-gray-600'} 
                    relative inline-flex h-6 w-11 items-center rounded-full transition-colors`}
                >
                  <span className={`${mode === 'default' ? 'translate-x-6' : 'translate-x-1'} 
                    inline-block h-4 w-4 transform rounded-full bg-white transition-transform`} />
                </Switch>
              </div>

              {/* Fairy Theme */}
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm ${mode === 'fairy' ? 'text-purple-200' : 'text-gray-400'}`}>
                    {t('settings.appearance.fairy_theme')}
                  </p>
                </div>
                <Switch
                  checked={mode === 'fairy'}
                  onChange={() => setTheme('fairy')}
                  className={`${mode === 'fairy' ? 'bg-purple-500' : 'bg-gray-600'} 
                    relative inline-flex h-6 w-11 items-center rounded-full transition-colors`}
                >
                  <span className={`${mode === 'fairy' ? 'translate-x-6' : 'translate-x-1'} 
                    inline-block h-4 w-4 transform rounded-full bg-white transition-transform`} />
                </Switch>
              </div>

              {/* Neon Theme */}
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm ${mode === 'neon' ? 'text-[#00FF00]' : 'text-gray-400'}`}>
                    {t('settings.appearance.neon_theme')}
                  </p>
                </div>
                <Switch
                  checked={mode === 'neon'}
                  onChange={() => setTheme('neon')}
                  className={`${mode === 'neon' ? 'bg-[#00FF00]' : 'bg-gray-600'} 
                    relative inline-flex h-6 w-11 items-center rounded-full transition-colors`}
                >
                  <span className={`${mode === 'neon' ? 'translate-x-6' : 'translate-x-1'} 
                    inline-block h-4 w-4 transform rounded-full bg-white transition-transform`} />
                </Switch>
              </div>
            </div>
          </div>

          {/* Notifications */}
          <div>
            <h3 className={`text-sm font-medium mb-2 ${isFairyTheme ? 'text-purple-200' : 'text-white'}`}>
              {t('settings.notifications.title')}
            </h3>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${isFairyTheme ? 'text-purple-200' : 'text-white'}`}>
                  {t('settings.notifications.push.title')}
                </p>
                <p className={`text-xs ${isFairyTheme ? 'text-purple-300' : 'text-gray-400'}`}>
                  {t('settings.notifications.push.description')}
                </p>
              </div>
              <Switch
                checked={pushNotificationsEnabled}
                onChange={setPushNotificationsEnabled}
                className={`${pushNotificationsEnabled ? (isFairyTheme ? 'bg-purple-500' : 'bg-primary') : 'bg-gray-600'} 
                  relative inline-flex h-6 w-11 items-center rounded-full transition-colors`}
              >
                <span className={`${pushNotificationsEnabled ? 'translate-x-6' : 'translate-x-1'} 
                  inline-block h-4 w-4 transform rounded-full bg-white transition-transform`} />
              </Switch>
            </div>
          </div>

          {/* About */}
          <div>
            <h3 className={`text-sm font-medium mb-2 ${isFairyTheme ? 'text-purple-200' : 'text-white'}`}>
              {t('settings.about.title')}
            </h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className={`text-sm ${isFairyTheme ? 'text-purple-300' : 'text-gray-400'}`}>
                  {t('settings.about.version')}
                </span>
                <span className={`text-sm ${isFairyTheme ? 'text-purple-200' : 'text-white'}`}>
                  1.0.0
                </span>
              </div>
              <div className={`text-xs ${isFairyTheme ? 'text-purple-300' : 'text-gray-400'}`}>
                {t('settings.about.powered_by')}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings; 